import React, { useState } from 'react';
import type { ProductWizardData } from '../../../hooks/useProductWizard';

interface ShippersSectionProps {
  shippers: ProductWizardData['shipping']['shippers'];
  onShippersChange: (shippers: ProductWizardData['shipping']['shippers']) => void;
}

export const ShippersSection: React.FC<ShippersSectionProps> = ({
  shippers,
  onShippersChange,
}) => {
  const [expandedShippers, setExpandedShippers] = useState<string[]>([]);

  // On mount, expand all enabled shippers
  React.useEffect(() => {
    const enabledIds = Object.keys(shippers || {}).filter(
      id => shippers[id].enabled
    );
    setExpandedShippers(enabledIds);
  }, []);  // Only run once on mount

  const handleShipperToggle = (shipperId: string) => {
    const shipper = shippers?.[shipperId];

    // If shipper has children, just toggle expand/collapse, don't change enabled state
    if (shipper?.children && shipper.children.length > 0) {
      setExpandedShippers((prev) =>
        prev.includes(shipperId)
          ? prev.filter((id) => id !== shipperId)
          : [...prev, shipperId]
      );
      return;
    }

    // For shippers without children, toggle enabled state normally
    const isCurrentlyEnabled = shipper?.enabled || false;
    const updatedShippers = {
      ...(shippers || {}),
      [shipperId]: {
        ...shipper,
        enabled: !isCurrentlyEnabled,
      },
    };

    onShippersChange(updatedShippers);
  };

  const handleChildToggle = (parentId: string, childId: string) => {
    const parent = shippers?.[parentId];

    if (!parent) return;

    // Update children
    const updatedChildren = parent.children?.map(child =>
      child.id === childId
        ? { ...child, enabled: !child.enabled }
        : child
    );

    // Parent is enabled if at least one child is enabled
    const hasEnabledChild = updatedChildren?.some(child => child.enabled) || false;

    const updatedShippers = {
      ...(shippers || {}),
      [parentId]: {
        ...parent,
        enabled: hasEnabledChild,
        children: updatedChildren
      }
    };

    onShippersChange(updatedShippers);
  };


  // No shippers loaded yet
  if (!shippers || Object.keys(shippers).length === 0) {
    return (
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 md:tw-p-6 tw-shadow-sm">
        <div className="tw-mb-6 tw-border-b tw-border-gray-200 tw-pb-4">
          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
            Shipping Companies & Services
          </h3>
          <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
            Configure available shipping options for your customers.
          </p>
        </div>
        <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
          <div className="tw-text-center">
            <span className="material-symbols-outlined tw-text-gray-400 tw-text-4xl tw-mb-2">
              local_shipping
            </span>
            <p className="tw-text-sm tw-text-gray-500">
              No shipping shippers available for this marketplace.
            </p>
            <p className="tw-text-xs tw-text-gray-400 tw-mt-1">
              Shippers may not be configured yet or failed to load.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 md:tw-p-6 tw-shadow-sm">
      <div className="tw-mb-6 tw-border-b tw-border-gray-200 tw-pb-4">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
          Shipping Companies & Services
        </h3>
        <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
          Configure available shipping options for your customers.
        </p>
      </div>

      <div className="tw-grid tw-gap-6">
        <div className="tw-flex tw-flex-col tw-gap-4">
          <label className="tw-text-sm tw-font-medium tw-text-gray-700">
            Available Shipping Shippers{" "}
            <span className="tw-text-red-500">*</span>
          </label>
          <p className="tw-text-xs tw-text-gray-500">
            Select shipping shippers to enable for this product.
          </p>

          <div className="tw-space-y-4">
            {Object.entries(shippers).map(([shipperId, shipperData]) => (
              <div
                key={shipperId}
                className="tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-rounded-lg tw-overflow-hidden"
              >
                {/* Parent Shipper */}
                <label
                  className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-cursor-pointer hover:tw-bg-gray-50 dark:hover:tw-bg-[#1a2531] tw-transition-all"
                  onClick={() => handleShipperToggle(shipperId)}
                >
                  {/* Checkbox */}
                  <div
                    className={`tw-w-5 tw-h-5 tw-border-2 tw-border-gray-300 dark:tw-border-gray-500 tw-rounded tw-flex tw-items-center tw-justify-center tw-transition-all ${
                      shipperData.enabled
                        ? "tw-border-primary tw-bg-primary"
                        : ""
                    }`}
                  >
                    <span
                      className={`material-symbols-outlined tw-text-white tw-text-sm tw-transition-opacity ${
                        shipperData.enabled
                          ? "tw-opacity-100"
                          : "tw-opacity-0"
                      }`}
                    >
                      check
                    </span>
                  </div>

                  {/* Icon and Name */}
                  <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1">
                    <span
                      className={`material-symbols-outlined tw-text-${shipperData.color || 'gray-600'}`}
                    >
                      {shipperData.icon || 'local_shipping'}
                    </span>
                    <div className="tw-flex tw-flex-col">
                      <span className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                        {shipperData.name}
                        {shipperData.isParent && shipperData.children && shipperData.children.length > 0 && (
                          <span className="tw-ml-2 tw-text-xs tw-text-gray-500">
                            ({shipperData.children.length} services)
                          </span>
                        )}
                      </span>
                      {shipperData.feeType && (
                        <span className="tw-text-xs tw-text-gray-500">
                          Fee type: {shipperData.feeType}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expand indicator - show if parent has children */}
                  {shipperData.children && shipperData.children.length > 0 && (
                    <span
                      className={`material-symbols-outlined tw-text-gray-400 tw-transition-transform ${
                        expandedShippers.includes(shipperId) ? 'tw-rotate-180' : ''
                      }`}
                    >
                      expand_more
                    </span>
                  )}
                </label>

                {/* Children section - only show if parent is expanded and has children */}
                {shipperData.children && shipperData.children.length > 0 && expandedShippers.includes(shipperId) && (
                  <div className="tw-border-t tw-border-gray-200 dark:tw-border-[#364152] tw-bg-gray-50/50 dark:tw-bg-[#151f28]/50 tw-p-4">
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                      {shipperData.children.map(child => (
                        <label
                          key={child.id}
                          className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-rounded-lg tw-cursor-pointer tw-transition-all hover:tw-border-primary hover:tw-bg-primary/5"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChildToggle(shipperId, child.id);
                          }}
                        >
                          {/* Child Checkbox */}
                          <div
                            className={`tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 dark:tw-border-gray-500 tw-rounded tw-flex tw-items-center tw-justify-center tw-transition-all ${
                              child.enabled
                                ? "tw-border-primary tw-bg-primary"
                                : ""
                            }`}
                          >
                            <span
                              className={`material-symbols-outlined tw-text-white tw-text-xs tw-transition-opacity ${
                                child.enabled
                                  ? "tw-opacity-100"
                                  : "tw-opacity-0"
                              }`}
                            >
                              check
                            </span>
                          </div>

                          {/* Child Name */}
                          <div className="tw-flex-1">
                            <span className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200">
                              {child.name}
                            </span>
                            <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400">
                              {child.feeType ? `Fee type: ${child.feeType}` : 'Standard delivery'}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
